import { useCallback, useEffect, useRef, useState } from 'react';

/* `beforeinstallprompt` isn't in lib.dom — Chromium-only event, no TS lib coverage. Cast locally
   rather than widen a global type. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallPromptState = 'unavailable' | 'available' | 'installed';

/** Exported for tests. True once the app is actually running as an installed PWA (launched from
    the home screen / app list), independent of whether this tab ever saw `beforeinstallprompt`. */
export function isStandaloneDisplayMode(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(display-mode: standalone)').matches;
}

export function useInstallPrompt(): {
  state: InstallPromptState;
  canInstall: boolean;
  isInstalled: boolean;
  promptInstall: () => Promise<void>;
} {
  const [state, setState] = useState<InstallPromptState>(() =>
    isStandaloneDisplayMode() ? 'installed' : 'unavailable',
  );
  const deferredEvent = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Already running standalone (or launched that way this session) — no point capturing
    // beforeinstallprompt, the browser won't fire it for an already-installed app anyway.
    if (isStandaloneDisplayMode()) return;

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      deferredEvent.current = event as BeforeInstallPromptEvent;
      setState('available');
    };
    const onAppInstalled = () => {
      deferredEvent.current = null;
      setState('installed');
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const event = deferredEvent.current;
    if (!event) return;
    // The captured event is single-use regardless of outcome — the browser won't let it be
    // prompted twice, so drop it either way rather than leaving a stale ref around.
    deferredEvent.current = null;
    await event.prompt();
    const { outcome } = await event.userChoice;
    setState(outcome === 'accepted' ? 'installed' : 'unavailable');
  }, []);

  return {
    state,
    canInstall: state === 'available',
    isInstalled: state === 'installed',
    promptInstall,
  };
}
